import React, { useState, useCallback, useContext, useEffect } from 'react';
import useStyles from './Login.styles';
import { Box, TextField, Typography, Button, SvgIcon } from '@material-ui/core';
import RedbullIcon from '../../assets/images/redbullIcon/RedbullIcon';
import { endpoints } from '../../utils/endpoints';
import { useParams } from 'react-router-dom';
import { SessionContext } from '../../contexts/session/SessionContext';
import useApi from '../../utils/useApi';
import { useTranslation } from 'react-i18next';
import tPaths from '../../utils/tPaths';
import { stylesConstants } from '../../styles/stylesConstants';
import RedbullLogoIcon from '../../assets/images/redbullIcon/RedBullLogoIcon';

const PasswordReset = () => {
  const styles = useStyles();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const { id } = useParams<{ id: string }>();
  const { updateToken } = useContext(SessionContext);
  const { post } = useApi(null);

  const { t } = useTranslation();

  useEffect(() => {
    document.title = 'WNDR password reset';
    return () => {
      document.title = 'WNDR admin panel';
    };
  }, []);

  const handleClickReset = useCallback(() => {
    if (newPassword.length < 8 || newPassword.length > 32) {
      setNewPasswordError(t(tPaths.inputFieldsErrors.passwordLengthError));
      return;
    }
    if (confirmPassword.length < 8 || confirmPassword.length > 32) {
      setConfirmPasswordError(t(tPaths.inputFieldsErrors.passwordLengthError));
      return;
    }
    if (newPassword !== confirmPassword) {
      setConfirmPasswordError(t(tPaths.inputFieldsErrors.passwordDoesNotMatch));
      return;
    }
    post({
      endpoint: endpoints.resetCustomerPassword,
      body: { newPassword, token: id },
      onSuccess: (response) => {
        updateToken(response?.data.accessToken);
        setShowSuccess(true);
      },
    });
  }, [confirmPassword, id, newPassword, post, t, updateToken]);

  const handleNewPasswordChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setNewPassword(value);
      setNewPasswordError(null);
    },
    [],
  );
  const handleConfirmPasswordChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setConfirmPassword(value);
      setConfirmPasswordError(null);
    },
    [],
  );

  return (
    <Box className={styles.background}>
      <Box
        className={styles.loginForm}
        display="flex"
        maxWidth={stylesConstants.inputFormWidth / 2 + 32}
        flex={0.8}
      >
        <Box alignSelf="center" margin={1} py={1}>
          {/* <SvgIcon component={RedbullIcon} /> */}
          <RedbullLogoIcon />
        </Box>
        <Box className={styles.inputContainer}>
          <Typography variant="h6">{t(tPaths.passwordReset.passwordReset)}</Typography>

          <Typography variant="subtitle2" className={styles.inputLabel}>
            {showSuccess ? t(tPaths.passwordReset.success) : t(tPaths.editFormLabels.newPassword)}
          </Typography>
          {!showSuccess && (
            <>
              <TextField
                onChange={handleNewPasswordChange}
                type="password"
                InputProps={{
                  classes: {
                    input: styles.inputs,
                  },
                }}
                error={Boolean(newPasswordError)}
                helperText={newPasswordError}
              />
              <Typography variant="subtitle2" className={styles.inputLabel}>
                {t(tPaths.editFormLabels.confirmPassword)}
              </Typography>
              <TextField
                onChange={handleConfirmPasswordChange}
                type="password"
                InputProps={{
                  classes: {
                    input: styles.inputs,
                  },
                }}
                error={Boolean(confirmPasswordError)}
                helperText={confirmPasswordError}
              />
            </>
          )}
        </Box>
        {!showSuccess && (
          <Button color="secondary" onClick={handleClickReset}>
            {t(tPaths.passwordReset.reset)}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default PasswordReset;
